import { useState, useEffect } from "react";
import { getPolicies, updatePolicies, getCategories, createCategory, updateCategory, deleteCategory, getUsers, updateUserRole, deleteUser, createUser, updateUser, updateUserSageAccount, updateUserSageNumEcriture } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import {
  Settings as SettingsIcon,
  Tag,
  Users,
  Car,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Shield,
  UserPlus,
  Mail,
  BadgeCheck,
  Building2,
  ImagePlus,
  X,
  BookOpen
} from "lucide-react";

const roleLabels = {
  collaborator: "Collaborateur",
  manager: "Manager",
  accountant: "Comptable",
  admin: "Administrateur"
};

const iconOptions = ["folder", "car", "utensils", "bed", "package", "phone", "graduation-cap", "gauge", "briefcase", "plane"];

export default function Settings() {
  const { user, hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ first_name: "", last_name: "", email: "", password: "", role: "collaborator" });
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ first_name: "", last_name: "", email: "", password: "", role: "collaborator" });
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    description: "",
    icon: "folder",
    color: "#6366f1",
    max_amount: "",
    requires_receipt: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [policyRes, categoriesRes, usersRes] = await Promise.all([
        getPolicies(),
        getCategories(),
        hasRole("admin") ? getUsers() : Promise.resolve({ data: [] })
      ]);
      setPolicy(policyRes.data);
      setCategories(categoriesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handlePolicySave = async () => {
    setSaving(true);
    try {
      await updatePolicies(policy);
      toast.success("Politique mise à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handleCategorySubmit = async () => {
    setSaving(true);
    try {
      const data = {
        ...categoryForm,
        max_amount: categoryForm.max_amount ? parseFloat(categoryForm.max_amount) : null
      };
      
      if (editingCategory) {
        await updateCategory(editingCategory.id, data);
        toast.success("Catégorie mise à jour");
      } else {
        await createCategory(data);
        toast.success("Catégorie créée");
      }
      
      setCategoryDialogOpen(false);
      resetCategoryForm();
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleCategoryDelete = async (category) => {
    if (!window.confirm(`Supprimer la catégorie "${category.name}" ?`)) return;
    try {
      await deleteCategory(category.id);
      toast.success("Catégorie supprimée");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleUserRoleChange = async (userId, newRole) => {
    try {
      await updateUserRole(userId, newRole);
      toast.success("Rôle mis à jour");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleUserDelete = async (u) => {
    if (!window.confirm(`Supprimer le compte de ${u.first_name} ${u.last_name} ? Cette action est irréversible.`)) return;
    try {
      await deleteUser(u.id);
      toast.success(`Compte de ${u.first_name} ${u.last_name} supprimé`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const handleSageAccountChange = async (userId, value) => {
    try {
      await updateUserSageAccount(userId, value || undefined);
      toast.success("Code comptable mis à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleSageNumEcritureChange = async (userId, value) => {
    try {
      await updateUserSageNumEcriture(userId, value || undefined);
      toast.success("Numéro d'écriture mis à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleOpenEditUser = (u) => {
    setEditingUser(u);
    setEditUserForm({ first_name: u.first_name, last_name: u.last_name, email: u.email, password: "", role: u.role });
    setEditUserDialogOpen(true);
  };

  const handleEditUserSubmit = async () => {
    setSaving(true);
    try {
      const payload = { ...editUserForm };
      if (!payload.password) delete payload.password;
      await updateUser(editingUser.id, payload);
      toast.success(`Compte de ${editUserForm.first_name} ${editUserForm.last_name} mis à jour`);
      setEditUserDialogOpen(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handleInviteSubmit = async () => {
    setSaving(true);
    try {
      await createUser(inviteForm);
      toast.success(`Compte créé pour ${inviteForm.first_name} ${inviteForm.last_name}`);
      setInviteDialogOpen(false);
      setInviteForm({ first_name: "", last_name: "", email: "", password: "", role: "collaborator" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  const resetCategoryForm = () => {
    setEditingCategory(null);
    setCategoryForm({
      name: "",
      description: "",
      icon: "folder",
      color: "#6366f1",
      max_amount: "",
      requires_receipt: true
    });
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || "",
      icon: category.icon,
      color: category.color,
      max_amount: category.max_amount ? String(category.max_amount) : "",
      requires_receipt: category.requires_receipt
    });
    setCategoryDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="page-title">Paramètres</h1>
        <p className="page-description">Configuration de l'application</p>
      </div>

      <Tabs defaultValue="policy">
        <TabsList>
          <TabsTrigger value="policy" className="gap-2" data-testid="policy-tab">
            <SettingsIcon className="h-4 w-4" />
            Politique
          </TabsTrigger>
          <TabsTrigger value="categories" className="gap-2" data-testid="categories-tab">
            <Tag className="h-4 w-4" />
            Catégories
          </TabsTrigger>
          {hasRole("admin") && (
            <TabsTrigger value="company" className="gap-2" data-testid="company-tab">
              <Building2 className="h-4 w-4" />
              Société
            </TabsTrigger>
          )}
          {hasRole(["admin", "accountant"]) && (
            <TabsTrigger value="accounting" className="gap-2" data-testid="accounting-tab">
              <BookOpen className="h-4 w-4" />
              Comptabilité
            </TabsTrigger>
          )}
          {hasRole("admin") && (
            <TabsTrigger value="users" className="gap-2" data-testid="users-tab">
              <Users className="h-4 w-4" />
              Utilisateurs
            </TabsTrigger>
          )}
        </TabsList>

        {/* Policy Tab */}
        <TabsContent value="policy" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Politique de dépenses</CardTitle>
              <CardDescription>
                Définissez les plafonds et règles de remboursement
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="daily_limit">Plafond journalier (€)</Label>
                  <Input
                    id="daily_limit"
                    type="number"
                    step="0.01"
                    value={policy?.daily_limit || ""}
                    onChange={(e) => setPolicy({ ...policy, daily_limit: parseFloat(e.target.value) || null })}
                    data-testid="daily-limit"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="monthly_limit">Plafond mensuel (€)</Label>
                  <Input
                    id="monthly_limit"
                    type="number"
                    step="0.01"
                    value={policy?.monthly_limit || ""}
                    onChange={(e) => setPolicy({ ...policy, monthly_limit: parseFloat(e.target.value) || null })}
                    data-testid="monthly-limit"
                  />
                </div>
              </div>

              <div className="space-y-4 p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-2">
                  <Car className="h-5 w-5 text-muted-foreground" />
                  <Label className="font-semibold">Barème kilométrique 2024 (DGFIP)</Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  Les indemnités sont calculées automatiquement selon le barème officiel en vigueur, en fonction du véhicule, des CV fiscaux et de la distance annuelle.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-1 pr-3 font-medium">Véhicule / CV</th>
                        <th className="text-right py-1 px-2 font-medium">≤ seuil 1</th>
                        <th className="text-right py-1 px-2 font-medium">Tranche 2</th>
                        <th className="text-right py-1 px-2 font-medium">Au-delà</th>
                      </tr>
                    </thead>
                    <tbody className="text-muted-foreground">
                      <tr className="border-b border-border/50"><td className="py-1 pr-3 font-medium text-foreground" colSpan={4}>Voiture — seuils : 5 000 / 20 000 km</td></tr>
                      <tr><td className="py-0.5 pr-3 pl-2">3 CV et moins</td><td className="text-right px-2">0,529 €/km</td><td className="text-right px-2">(d × 0,316) + 1 065</td><td className="text-right px-2">0,370 €/km</td></tr>
                      <tr><td className="py-0.5 pr-3 pl-2">4 CV</td><td className="text-right px-2">0,606 €/km</td><td className="text-right px-2">(d × 0,340) + 1 330</td><td className="text-right px-2">0,407 €/km</td></tr>
                      <tr><td className="py-0.5 pr-3 pl-2">5 CV</td><td className="text-right px-2">0,636 €/km</td><td className="text-right px-2">(d × 0,357) + 1 395</td><td className="text-right px-2">0,427 €/km</td></tr>
                      <tr><td className="py-0.5 pr-3 pl-2">6 CV</td><td className="text-right px-2">0,665 €/km</td><td className="text-right px-2">(d × 0,374) + 1 457</td><td className="text-right px-2">0,447 €/km</td></tr>
                      <tr className="border-b border-border/50"><td className="py-0.5 pr-3 pl-2">7 CV et plus</td><td className="text-right px-2">0,697 €/km</td><td className="text-right px-2">(d × 0,394) + 1 515</td><td className="text-right px-2">0,470 €/km</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1 pr-3 font-medium text-foreground" colSpan={4}>Moto (&gt; 50 cm³) — seuils : 3 000 / 6 000 km</td></tr>
                      <tr><td className="py-0.5 pr-3 pl-2">1 ou 2 CV</td><td className="text-right px-2">0,395 €/km</td><td className="text-right px-2">(d × 0,099) + 891</td><td className="text-right px-2">0,248 €/km</td></tr>
                      <tr><td className="py-0.5 pr-3 pl-2">3 à 5 CV</td><td className="text-right px-2">0,468 €/km</td><td className="text-right px-2">(d × 0,082) + 1 158</td><td className="text-right px-2">0,275 €/km</td></tr>
                      <tr className="border-b border-border/50"><td className="py-0.5 pr-3 pl-2">Plus de 5 CV</td><td className="text-right px-2">0,606 €/km</td><td className="text-right px-2">(d × 0,079) + 1 581</td><td className="text-right px-2">0,343 €/km</td></tr>
                      <tr className="border-b border-border/50"><td className="py-1 pr-3 font-medium text-foreground" colSpan={4}>Scooter / Vélomoteur (≤ 50 cm³) — seuils : 3 000 / 6 000 km</td></tr>
                      <tr><td className="py-0.5 pr-3 pl-2 text-muted-foreground" colSpan={1}>Toutes cylindrées</td><td className="text-right px-2">0,272 €/km</td><td className="text-right px-2">(d × 0,064) + 624</td><td className="text-right px-2">0,168 €/km</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="approval_threshold">Seuil d'approbation (€)</Label>
                <Input
                  id="approval_threshold"
                  type="number"
                  step="0.01"
                  value={policy?.requires_approval_above || ""}
                  onChange={(e) => setPolicy({ ...policy, requires_approval_above: parseFloat(e.target.value) || 0 })}
                  data-testid="approval-threshold"
                />
                <p className="text-xs text-muted-foreground">
                  Les dépenses supérieures à ce montant nécessitent une approbation
                </p>
              </div>

              <Button onClick={handlePolicySave} disabled={saving} data-testid="save-policy">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Enregistrer les modifications
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Catégories de dépenses</CardTitle>
                <CardDescription>Gérez les types de dépenses disponibles</CardDescription>
              </div>
              <Dialog open={categoryDialogOpen} onOpenChange={(open) => {
                setCategoryDialogOpen(open);
                if (!open) resetCategoryForm();
              }}>
                <DialogTrigger asChild>
                  <Button className="gap-2" data-testid="add-category">
                    <Plus className="h-4 w-4" />
                    Ajouter
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingCategory ? "Modifier la catégorie" : "Nouvelle catégorie"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Nom</Label>
                      <Input
                        value={categoryForm.name}
                        onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                        placeholder="Ex: Transport"
                        data-testid="category-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={categoryForm.description}
                        onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                        placeholder="Description optionnelle"
                        rows={2}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Icône</Label>
                        <Select
                          value={categoryForm.icon}
                          onValueChange={(value) => setCategoryForm({ ...categoryForm, icon: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {iconOptions.map((icon) => (
                              <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Couleur</Label>
                        <Input
                          type="color"
                          value={categoryForm.color}
                          onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Plafond (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={categoryForm.max_amount}
                        onChange={(e) => setCategoryForm({ ...categoryForm, max_amount: e.target.value })}
                        placeholder="Optionnel"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Justificatif requis</Label>
                      <Switch
                        checked={categoryForm.requires_receipt}
                        onCheckedChange={(checked) => setCategoryForm({ ...categoryForm, requires_receipt: checked })}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>
                      Annuler
                    </Button>
                    <Button onClick={handleCategorySubmit} disabled={saving || !categoryForm.name}>
                      {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      {editingCategory ? "Mettre à jour" : "Créer"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Catégorie</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Plafond</TableHead>
                    <TableHead>Justificatif</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map((category) => (
                    <TableRow key={category.id} data-testid={`category-row-${category.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: category.color }}
                          />
                          <span className="font-medium">{category.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {category.description || "-"}
                      </TableCell>
                      <TableCell>
                        {category.max_amount 
                          ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(category.max_amount)
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {category.requires_receipt ? "Oui" : "Non"}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditCategory(category)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => handleCategoryDelete(category)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Accounting Tab */}
        {hasRole(["admin", "accountant"]) && (
          <TabsContent value="accounting" className="mt-6 space-y-6">

            {/* Paramètres généraux journal */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Journal et comptes généraux</CardTitle>
                <CardDescription>Paramètres utilisés lors de l'export FEC / SAGE</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Code journal</Label>
                    <Input
                      value={policy?.sage_journal_code ?? "NDF"}
                      onChange={(e) => setPolicy({ ...policy, sage_journal_code: e.target.value })}
                      placeholder="NDF"
                      data-testid="sage-journal-code"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Libellé journal</Label>
                    <Input
                      value={policy?.sage_journal_lib ?? "Notes de frais"}
                      onChange={(e) => setPolicy({ ...policy, sage_journal_lib: e.target.value })}
                      placeholder="Notes de frais"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Compte TVA par défaut (fallback)</Label>
                    <Input
                      value={policy?.sage_vat_account ?? "44566"}
                      onChange={(e) => setPolicy({ ...policy, sage_vat_account: e.target.value })}
                      placeholder="44566"
                      data-testid="sage-vat-account"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Libellé compte TVA par défaut</Label>
                    <Input
                      value={policy?.sage_vat_lib ?? "TVA déductible sur autres biens et services"}
                      onChange={(e) => setPolicy({ ...policy, sage_vat_lib: e.target.value })}
                      placeholder="TVA déductible…"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Préfixe compte collaborateur</Label>
                    <Input
                      value={policy?.sage_employee_prefix ?? "421"}
                      onChange={(e) => setPolicy({ ...policy, sage_employee_prefix: e.target.value })}
                      placeholder="421"
                      data-testid="sage-employee-prefix"
                    />
                    <p className="text-xs text-muted-foreground">Le compte final sera : préfixe + identifiant collaborateur (ex : 421ABC12)</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Compte de charge par défaut</Label>
                    <Input
                      value={policy?.sage_default_account ?? "6288"}
                      onChange={(e) => setPolicy({ ...policy, sage_default_account: e.target.value })}
                      placeholder="6288"
                      data-testid="sage-default-account"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Libellé compte par défaut</Label>
                    <Input
                      value={policy?.sage_default_lib ?? "Frais divers de gestion"}
                      onChange={(e) => setPolicy({ ...policy, sage_default_lib: e.target.value })}
                      placeholder="Frais divers de gestion"
                    />
                  </div>
                </div>
                <Button onClick={handlePolicySave} disabled={saving} data-testid="save-accounting">
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Enregistrer
                </Button>
              </CardContent>
            </Card>

            {/* Comptes TVA par taux */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Comptes TVA par taux</CardTitle>
                <CardDescription>Définissez un compte TVA déductible distinct pour chaque taux. Si vide, le compte par défaut est utilisé.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[110px]">Taux TVA</TableHead>
                      <TableHead className="w-[160px]">N° de compte</TableHead>
                      <TableHead>Libellé comptable</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[
                      { rate: "20.0", label: "20 %" },
                      { rate: "10.0", label: "10 %" },
                      { rate: "5.5",  label: "5,5 %" },
                      { rate: "2.1",  label: "2,1 %" },
                    ].map(({ rate, label }) => {
                      const vatAccounts = policy?.sage_vat_accounts ?? {};
                      const entry = vatAccounts[rate] ?? {};
                      return (
                        <TableRow key={rate}>
                          <TableCell className="font-medium">{label}</TableCell>
                          <TableCell>
                            <Input
                              className="h-8 text-sm"
                              value={entry.account ?? ""}
                              placeholder={policy?.sage_vat_account ?? "44566"}
                              onChange={(e) => setPolicy({
                                ...policy,
                                sage_vat_accounts: {
                                  ...(policy?.sage_vat_accounts ?? {}),
                                  [rate]: { ...entry, account: e.target.value }
                                }
                              })}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              className="h-8 text-sm"
                              value={entry.lib ?? ""}
                              placeholder={policy?.sage_vat_lib ?? "TVA déductible…"}
                              onChange={(e) => setPolicy({
                                ...policy,
                                sage_vat_accounts: {
                                  ...(policy?.sage_vat_accounts ?? {}),
                                  [rate]: { ...entry, lib: e.target.value }
                                }
                              })}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <div className="p-4 border-t">
                  <Button onClick={handlePolicySave} disabled={saving}>
                    {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Enregistrer les comptes TVA
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Comptes par catégorie */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Comptes de charges par catégorie</CardTitle>
                <CardDescription>Associez un compte PCG à chaque catégorie de dépense</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Catégorie</TableHead>
                      <TableHead className="w-[160px]">N° de compte</TableHead>
                      <TableHead>Libellé comptable</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categories.map((cat) => {
                      const catAccount = (policy?.sage_category_accounts ?? {})[cat.id] ?? {};
                      return (
                        <TableRow key={cat.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cat.color }} />
                              <span className="font-medium">{cat.name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Input
                              className="h-8 text-sm"
                              value={catAccount.account ?? ""}
                              placeholder={policy?.sage_default_account ?? "6288"}
                              onChange={(e) => setPolicy({
                                ...policy,
                                sage_category_accounts: {
                                  ...(policy?.sage_category_accounts ?? {}),
                                  [cat.id]: { ...catAccount, account: e.target.value }
                                }
                              })}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              className="h-8 text-sm"
                              value={catAccount.lib ?? ""}
                              placeholder={policy?.sage_default_lib ?? "Frais divers de gestion"}
                              onChange={(e) => setPolicy({
                                ...policy,
                                sage_category_accounts: {
                                  ...(policy?.sage_category_accounts ?? {}),
                                  [cat.id]: { ...catAccount, lib: e.target.value }
                                }
                              })}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <div className="p-4 border-t">
                  <Button onClick={handlePolicySave} disabled={saving} data-testid="save-category-accounts">
                    {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Enregistrer les comptes
                  </Button>
                </div>
              </CardContent>
            </Card>

          </TabsContent>
        )}

        {/* Company Tab */}
        {hasRole("admin") && (
          <TabsContent value="company" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Identité de la société</CardTitle>
                <CardDescription>Personnalisez le logo et le nom affiché dans la barre latérale</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Nom de la société</Label>
                  <Input
                    id="company_name"
                    value={policy?.company_name || ""}
                    onChange={(e) => setPolicy({ ...policy, company_name: e.target.value })}
                    placeholder="Ex : Acme Corp"
                    data-testid="company-name"
                  />
                </div>

                <div className="space-y-3">
                  <Label>Logo</Label>
                  {policy?.company_logo ? (
                    <div className="flex items-center gap-4">
                      <img
                        src={policy.company_logo}
                        alt="Logo société"
                        className="h-12 w-auto max-w-[160px] object-contain rounded border p-1"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2 text-destructive hover:text-destructive"
                        onClick={() => setPolicy({ ...policy, company_logo: null })}
                      >
                        <X className="h-4 w-4" />
                        Supprimer
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-colors">
                      <ImagePlus className="h-8 w-8 text-muted-foreground mb-2" />
                      <span className="text-sm text-muted-foreground">Cliquer pour uploader un logo</span>
                      <span className="text-xs text-muted-foreground mt-1">PNG, JPG, SVG — max 500 ko</span>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        className="hidden"
                        data-testid="company-logo-input"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 500 * 1024) {
                            toast.error("Le logo ne doit pas dépasser 500 ko");
                            return;
                          }
                          const reader = new FileReader();
                          reader.onload = (ev) => setPolicy({ ...policy, company_logo: ev.target.result });
                          reader.readAsDataURL(file);
                        }}
                      />
                    </label>
                  )}
                </div>

                <Button onClick={handlePolicySave} disabled={saving} data-testid="save-company">
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Enregistrer
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Users Tab */}
        {hasRole("admin") && (
          <TabsContent value="users" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Gestion des collaborateurs</CardTitle>
                  <CardDescription>Invitez, gérez les rôles et supprimez des comptes</CardDescription>
                </div>
                <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="gap-2" data-testid="invite-user">
                      <UserPlus className="h-4 w-4" />
                      Inviter
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Créer un compte collaborateur</DialogTitle>
                      <DialogDescription>Le collaborateur pourra se connecter avec ces identifiants.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Prénom</Label>
                          <Input
                            value={inviteForm.first_name}
                            onChange={(e) => setInviteForm({ ...inviteForm, first_name: e.target.value })}
                            placeholder="Prénom"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Nom</Label>
                          <Input
                            value={inviteForm.last_name}
                            onChange={(e) => setInviteForm({ ...inviteForm, last_name: e.target.value })}
                            placeholder="Nom"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={inviteForm.email}
                          onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                          placeholder="email@entreprise.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Mot de passe</Label>
                        <Input
                          type="password"
                          value={inviteForm.password}
                          onChange={(e) => setInviteForm({ ...inviteForm, password: e.target.value })}
                          placeholder="••••••••"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Rôle</Label>
                        <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="collaborator">Collaborateur</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="accountant">Comptable</SelectItem>
                            <SelectItem value="admin">Administrateur</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>Annuler</Button>
                      <Button
                        onClick={handleInviteSubmit}
                        disabled={saving || !inviteForm.first_name || !inviteForm.last_name || !inviteForm.email || !inviteForm.password}
                      >
                        {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Créer le compte
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Collaborateur</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Véhicule</TableHead>
                      <TableHead className="w-[150px]">Code comptable</TableHead>
                      <TableHead className="w-[150px]">Numéro d'écriture</TableHead>
                      <TableHead>Rôle</TableHead>
                      <TableHead>Inscrit le</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                        <TableCell className="font-medium">
                          <div>
                            {u.first_name} {u.last_name}
                            {u.department && (
                              <p className="text-xs text-muted-foreground">{u.department}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{u.email}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {u.vehicle_type ? `${u.vehicle_type}${u.cv_fiscal ? ` · ${u.cv_fiscal}` : ""}` : "—"}
                        </TableCell>
                        <TableCell>
                          <Input
                            className="h-8 text-sm w-[130px]"
                            defaultValue={u.sage_account ?? ""}
                            placeholder={`421${(u.id || "").slice(0, 5).toUpperCase()}`}
                            onBlur={(e) => {
                              if (e.target.value !== (u.sage_account ?? "")) {
                                handleSageAccountChange(u.id, e.target.value);
                              }
                            }}
                            data-testid={`sage-account-${u.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            className="h-8 text-sm w-[130px]"
                            defaultValue={u.sage_num_ecriture ?? ""}
                            placeholder="Ex : 001"
                            onBlur={(e) => {
                              if (e.target.value !== (u.sage_num_ecriture ?? "")) {
                                handleSageNumEcritureChange(u.id, e.target.value);
                              }
                            }}
                            data-testid={`sage-num-ecriture-${u.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            value={u.role}
                            onValueChange={(value) => handleUserRoleChange(u.id, value)}
                            disabled={u.id === user?.id}
                          >
                            <SelectTrigger className="w-[150px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="collaborator">Collaborateur</SelectItem>
                              <SelectItem value="manager">Manager</SelectItem>
                              <SelectItem value="accountant">Comptable</SelectItem>
                              <SelectItem value="admin">Administrateur</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(u.created_at).toLocaleDateString('fr-FR')}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenEditUser(u)}
                              data-testid={`edit-user-${u.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {u.id !== user?.id && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive hover:text-destructive"
                                onClick={() => handleUserDelete(u)}
                                data-testid={`delete-user-${u.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Dialog modification utilisateur */}
      <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier l'utilisateur</DialogTitle>
            <DialogDescription>
              Modifiez les informations du compte. Laissez le mot de passe vide pour ne pas le changer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Prénom</Label>
                <Input
                  value={editUserForm.first_name}
                  onChange={(e) => setEditUserForm({ ...editUserForm, first_name: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>Nom</Label>
                <Input
                  value={editUserForm.last_name}
                  onChange={(e) => setEditUserForm({ ...editUserForm, last_name: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Email</Label>
              <Input
                type="email"
                value={editUserForm.email}
                onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label>Nouveau mot de passe <span className="text-muted-foreground text-xs">(laisser vide pour ne pas modifier)</span></Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={editUserForm.password}
                onChange={(e) => setEditUserForm({ ...editUserForm, password: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label>Rôle</Label>
              <Select
                value={editUserForm.role}
                onValueChange={(value) => setEditUserForm({ ...editUserForm, role: value })}
                disabled={editingUser?.id === user?.id}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="collaborator">Collaborateur</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="accountant">Comptable</SelectItem>
                  <SelectItem value="admin">Administrateur</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserDialogOpen(false)}>Annuler</Button>
            <Button
              onClick={handleEditUserSubmit}
              disabled={saving || !editUserForm.first_name || !editUserForm.last_name || !editUserForm.email}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
